import React from "react";
import { Button, ButtonType } from "components/common/Button.component";
import { FormField } from "components/common/FormField.component";
import OKCancelButtons from "components/modals/OKCancelButtons";
import UserSearch, { ExceptionsRoles, FindEmployees } from "components/input/UserSearch.component";
import { MODAL_CONTENT_TAB_INDEX } from "utils/constants";
import {
    ExceptionFormProps,
    ExceptionRoleOption,
    ExceptionRow,
    ExceptionUserOption,
} from "types/modals";

// An exception row is complete once it has a requester, a role and at least one approver.
export const isExceptionSaveEnabled = (
    requester?: ExceptionUserOption | null,
    role?: ExceptionRoleOption | null,
    approvers?: ExceptionUserOption[] | null,
): boolean => Boolean(requester) && Boolean(role) && Boolean(approvers && approvers.length > 0);

// Maps the selected values into the row shape consumed by the exceptions list.
export const buildExceptionRow = (
    requester?: ExceptionUserOption | null,
    role?: ExceptionRoleOption | null,
    approvers?: ExceptionUserOption[] | null,
): ExceptionRow => ({
    valid: requester?.valid,
    label: requester?.label,
    value: requester?.value,
    users: (approvers ?? []).map((user) => ({
        valid: user.valid,
        value: user.value,
        label: user.label,
    })),
    role: role ?? undefined,
});

// Shared body + footer for the add / edit exception modals. The only differences between the
// two flows are whether the requester/role fields are editable and the primary button label.
export const ExceptionForm = ({
    translate,
    requester,
    role,
    approvers,
    filterValues,
    onApproversChange,
    onRequesterChange,
    onRoleChange,
    requesterRoleDisabled,
    focusRequester,
    maxMenuHeight = 150,
    hideClose,
    okText,
    isSaveEnabled,
    onOk,
    onCancel,
    loopFocus,
}: ExceptionFormProps): React.ReactElement => (
    <>
        <div className="ga-modal__content add-new-exception-popup">
            <FormField
                id="requester-search"
                label={translate("workflowDetails.exceptions.requester")}
                labelState={`(${translate("popUp.required")})`}
                disabled={requesterRoleDisabled}
            >
                <FindEmployees
                    disabled={requesterRoleDisabled}
                    propagateValue={onRequesterChange}
                    className="ast_find_employees"
                    filterValues={filterValues}
                    value={requester}
                    refresh={focusRequester}
                    tabindex={MODAL_CONTENT_TAB_INDEX}
                    tabSelectsValue={false}
                    blurInputOnSelect={false}
                    translate={translate}
                    menuPosition={"fixed"}
                    menuPortalTarget={document.body}
                />
            </FormField>
            <FormField
                id="role-search"
                label={translate("workflowDetails.exceptions.role")}
                labelState={`(${translate("popUp.required")})`}
                disabled={requesterRoleDisabled}
            >
                <ExceptionsRoles
                    disabled={requesterRoleDisabled}
                    propagateValue={onRoleChange}
                    value={role}
                    tabindex={MODAL_CONTENT_TAB_INDEX + 1}
                    tabSelectsValue={false}
                    blurInputOnSelect={false}
                    translate={translate}
                    menuPosition={"fixed"}
                    menuPortalTarget={document.body}
                />
            </FormField>
            <FormField
                id="approvers-search"
                label={translate("workflowDetails.exceptions.approvers")}
                labelState={`(${translate("popUp.required")})`}
            >
                <UserSearch
                    multi={true}
                    propagateValue={onApproversChange}
                    value={approvers}
                    includeAdminApprover={true}
                    maxMenuHeight={maxMenuHeight}
                    tabindex={MODAL_CONTENT_TAB_INDEX + 2}
                    tabSelectsValue={false}
                    blurInputOnSelect={false}
                    translate={translate}
                    menuPlacement={"top"}
                    menuPosition={"fixed"}
                    menuPortalTarget={document.body}
                />
            </FormField>
        </div>

        {hideClose ? (
            <div className="ga-modal__actions">
                <Button
                    type={ButtonType.PRIMARY}
                    onClick={onOk}
                    onBlur={loopFocus}
                    disabled={!isSaveEnabled}
                    tabIndex={MODAL_CONTENT_TAB_INDEX + 3}
                    text={okText}
                />
            </div>
        ) : (
            <OKCancelButtons
                tabIndex={MODAL_CONTENT_TAB_INDEX + 3}
                okEnabled={isSaveEnabled}
                okCallback={onOk}
                okText={okText}
                cancelEnabled={true}
                cancelCallback={onCancel}
                cancelText={translate("popUp.cancel")}
                loopFocus={loopFocus}
            />
        )}
    </>
);
